import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ConversationContext {
  activeCompanyId: string | null;
  activeCompanyName: string | null;
  recentKPIs: string[];
  lastIntent: string | null;
  conversationHistory: Message[];
}

const STORAGE_KEY = "memo-conversation-context";
const MAX_HISTORY = 10;

export function useConversationContext() {
  const [context, setContext] = useState<ConversationContext>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          conversationHistory: parsed.conversationHistory?.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          })) || [],
        };
      }
    } catch {
      // Ignore parse errors
    }
    return {
      activeCompanyId: null,
      activeCompanyName: null,
      recentKPIs: [],
      lastIntent: null,
      conversationHistory: [],
    };
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    } catch {
      // Ignore storage errors
    }
  }, [context]);

  const setActiveCompany = useCallback((companyId: string | null, companyName: string | null) => {
    setContext(prev => ({
      ...prev,
      activeCompanyId: companyId,
      activeCompanyName: companyName,
    }));
  }, []);

  const addMessage = useCallback((message: Omit<Message, "id" | "timestamp">) => {
    const newMessage: Message = {
      ...message,
      id: `${message.role}-${Date.now()}`,
      timestamp: new Date(),
    };

    setContext(prev => ({
      ...prev,
      conversationHistory: [...prev.conversationHistory.slice(-MAX_HISTORY + 1), newMessage],
    }));

    return newMessage;
  }, []);

  const updateLastIntent = useCallback((intent: string) => {
    setContext(prev => ({
      ...prev,
      lastIntent: intent,
    }));
  }, []);

  const addRecentKPI = useCallback((kpiName: string) => {
    setContext(prev => ({
      ...prev,
      recentKPIs: [kpiName, ...prev.recentKPIs.filter(k => k !== kpiName)].slice(0, 5),
    }));
  }, []);

  const clearConversation = useCallback(() => {
    setContext(prev => ({
      ...prev,
      conversationHistory: [],
      lastIntent: null,
    }));
  }, []);

  const clearAll = useCallback(() => {
    setContext({
      activeCompanyId: null,
      activeCompanyName: null,
      recentKPIs: [],
      lastIntent: null,
      conversationHistory: [],
    });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Get context as a summary string for the AI
  const getContextSummary = useCallback(() => {
    const parts: string[] = [];
    
    if (context.activeCompanyName) {
      parts.push(`Currently viewing: ${context.activeCompanyName}`);
    }
    
    if (context.recentKPIs.length > 0) {
      parts.push(`Recent KPIs: ${context.recentKPIs.join(", ")}`);
    }
    
    if (context.lastIntent) {
      parts.push(`Last action: ${context.lastIntent}`);
    }

    if (context.conversationHistory.length > 0) {
      const recentMessages = context.conversationHistory.slice(-3);
      parts.push(`Recent messages: ${recentMessages.map(m => `${m.role}: "${m.content.slice(0, 50)}..."`).join(" | ")}`);
    }

    return parts.join(". ");
  }, [context]);

  return {
    context,
    setActiveCompany,
    addMessage,
    updateLastIntent,
    addRecentKPI,
    clearConversation,
    clearAll,
    getContextSummary,
  };
}
